import { ChangeEvent, EventHandler, useEffect, KeyboardEvent, useState } from 'react'
import { useDebounceValue } from 'usehooks-ts'
import { DecodedValueMap, QueryParamConfigMap, SetQuery } from 'use-query-params'

import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { useQuery } from '@percent/admin-dashboard/common/hooks'

type UsePartnerFilterProps<T extends QueryParamConfigMap> = {
  queryParams: DecodedValueMap<T>
  setQueryParams: SetQuery<T>
}

export const usePartnerFilter = <T extends QueryParamConfigMap>({
  queryParams,
  setQueryParams
}: UsePartnerFilterProps<T>) => {
  const [selectedPartner, setSelectedPartner] = useState<string>(queryParams?.partnerId ?? '')
  const [partnerNameSearch, setPartnerNameSearch] = useState<string>('')
  const [debouncedSearch] = useDebounceValue(partnerNameSearch, 500)

  const { adminService } = useServices()

  const [{ data, isLoading }, { query }] = useQuery(adminService.queries.getPartnerList, {
    query: debouncedSearch,
    partnerIds: selectedPartner ? [selectedPartner] : undefined
  })

  useEffect(() => {
    query({ query: debouncedSearch, partnerIds: [...selectedPartner] })
  }, [debouncedSearch, query, selectedPartner])

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPartnerNameSearch(event.target.value)
  }

  const handleClearSearchValue = () => {
    setPartnerNameSearch('')
  }

  const handleKeyPress: EventHandler<KeyboardEvent<HTMLInputElement>> = event => {
    if (event.key === 'Enter') {
      setPartnerNameSearch((event.target as HTMLInputElement).value)
    }

    if (event.key === 'Escape') {
      setPartnerNameSearch('')
    }
  }

  const onPartnerChange = (partners: string[]) => {
    if (partners.length === 0) {
      setQueryParams({ partnerId: undefined } as Partial<DecodedValueMap<T>>)

      if (!debouncedSearch) {
        query({
          query: debouncedSearch,
          partnerIds: undefined
        })
      }
    }
    setSelectedPartner(partners.length === 0 ? '' : partners.filter(p => p !== selectedPartner)[0])
  }

  const onPartnersSearchButtonClick = () => {
    setQueryParams({ partnerId: selectedPartner } as Partial<DecodedValueMap<T>>)
  }

  return {
    selectedPartner,
    partnerNameOptions: data?.map(p => ({ label: p.name, value: p.id })) || [],
    onPartnerChange,
    isPartnerNameFilterLoading: isLoading,
    partnerSearch: {
      onPartnerSearchChange: handleSearchChange,
      onClearPartnerSearchValue: handleClearSearchValue,
      partnerNameSearch,
      onPartnersSearchButtonClick,
      handleKeyPress
    }
  }
}
